import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import {
  LayoutDashboard, User, Users, ShieldCheck, GraduationCap, Building2,
  LogOut, Menu, X, Home, ClipboardList, CreditCard, CalendarDays, Ticket, Receipt, Newspaper, ShoppingBag, Package, ArrowLeft, MessageSquare, HeartHandshake, Radio, Wallet, ClipboardCheck, Star, MessageSquareHeart, ChevronDown, Contact, Images, BookOpen, FileText, Sparkles, Coins, Wand2, PenLine, Inbox as InboxIcon
, BellRing, PhoneCall } from 'lucide-react';
import oshoLogo from '../assets/osho-logo.png';

const DashboardLayout = () => {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const isAdmin = hasRole('admin');

  useEffect(() => {
    if (!isAdmin) return undefined;
    const load = () => api.get('/admin/crm/inbox-unread').then((r) => setUnread(r.data?.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [isAdmin, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  // Personal (all members) — always visible, ungrouped.
  const personal = [
    { to: '/dashboard', label: 'Overview', icon: LayoutDashboard, end: true },
    { to: '/dashboard/profile', label: 'My Profile', icon: User },
    { to: '/dashboard/bookings', label: 'My Bookings', icon: Ticket },
    { to: '/dashboard/orders', label: 'My Orders', icon: Package },
    { to: '/dashboard/payments', label: 'Payment History', icon: Receipt },
    { to: '/dashboard/centers', label: 'My Centers', icon: Building2 },
    { to: '/dashboard/facilitator', label: 'Facilitator / Teacher', icon: GraduationCap },
    { to: '/dashboard/share-experience', label: 'Share Experience', icon: MessageSquareHeart },
  ];
  if (hasRole('content_contributor') && !hasRole('admin')) {
    personal.push({ to: '/dashboard/studio', label: 'Content Studio', icon: PenLine });
  }
  if (hasRole('ashram_coord', 'center_coord')) {
    personal.push({ to: '/dashboard/coordinator', label: 'Coordinator', icon: Building2 });
  }
  if (hasRole('admin', 'facilitator', 'ashram_coord', 'center_coord')) {
    personal.push({ to: '/dashboard/programs', label: 'My Programs', icon: CalendarDays });
    personal.push({ to: '/dashboard/consultations', label: 'Consultation Requests', icon: ClipboardList });
  }
  if (hasRole('facilitator') && !hasRole('admin')) {
    personal.push({ to: '/dashboard/my-live', label: 'My Live Sessions', icon: Radio });
  }

  // Admin — grouped accordions.
  const adminGroups = hasRole('admin') ? [
    { id: 'dashboard', label: 'Dashboard', items: [
      { to: '/dashboard/admin', label: 'Admin Overview', icon: ShieldCheck, end: true },
    ] },
    { id: 'people', label: 'People (CRM)', items: [
      { to: '/dashboard/admin/customers', label: 'Customers', icon: Contact },
      { to: '/dashboard/admin/inbox', label: 'Inbox', icon: InboxIcon, badge: 'inbox' },
      { to: '/dashboard/admin/users', label: 'Users & Roles', icon: Users },
      { to: '/dashboard/admin/teachers', label: 'Facilitators & Teachers', icon: GraduationCap },
      { to: '/dashboard/admin/approvals', label: 'Coordinator Approvals', icon: ClipboardList },
    ] },
    { id: 'programs', label: 'Programs & Events', items: [
      { to: '/dashboard/admin/programs', label: 'Programs', icon: CalendarDays },
      { to: '/dashboard/admin/checkin', label: 'Registrants & Check-in', icon: ClipboardCheck },
      { to: '/dashboard/admin/enquiries', label: 'Registration Requests', icon: PhoneCall },
      { to: '/dashboard/admin/event-photos', label: 'Event Photos', icon: Images },
      { to: '/dashboard/admin/live', label: 'Live Streaming', icon: Radio },
      { to: '/dashboard/admin/notifications', label: 'Push Notifications', icon: BellRing },
      { to: '/dashboard/admin/consultations', label: 'Consultations', icon: ClipboardList },
    ] },
    { id: 'commerce', label: 'Commerce', items: [
      { to: '/dashboard/admin/payments', label: 'Payments & Orders', icon: Wallet },
      { to: '/dashboard/admin/shop', label: 'Eco-Store', icon: ShoppingBag },
      { to: '/dashboard/admin/donations', label: 'Donations', icon: HeartHandshake },
      { to: '/dashboard/admin/memberships', label: 'Memberships', icon: CreditCard },
    ] },
    { id: 'content', label: 'Content & Community', items: [
      { to: '/dashboard/admin/home-hero', label: 'Home Hero', icon: Home },
      { to: '/dashboard/admin/meditations', label: 'Meditations', icon: Star },
      { to: '/dashboard/admin/library', label: 'Osho Library', icon: BookOpen },
      { to: '/dashboard/admin/legal', label: 'Legal Pages', icon: FileText },
      { to: '/dashboard/admin/chat', label: 'AI Chat', icon: Sparkles },
      { to: '/dashboard/admin/assistant', label: 'AI Assistant', icon: Wand2 },
      { to: '/dashboard/admin/ai-usage', label: 'AI Usage', icon: Coins },
      { to: '/dashboard/admin/blog', label: 'Blog & News', icon: Newspaper },
      { to: '/dashboard/admin/testimonials', label: 'Testimonials & Feedback', icon: Star },
      { to: '/dashboard/admin/community', label: 'Community', icon: MessageSquare },
      { to: '/dashboard/admin/centers', label: 'Centers', icon: Building2 },
    ] },
  ] : [];

  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    adminGroups.forEach((g) => { init[g.id] = true; });
    return init;
  });
  const toggleGroup = (id) => setOpenGroups((s) => ({ ...s, [id]: !s[id] }));

  const linkClass = ({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? 'bg-rose-soft text-mahogany font-medium' : 'text-burgundy/85 hover:bg-rose-soft/50'}`;

  const initials = (user?.full_name || user?.email || '?').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-cream flex" data-no-translate>
      {/* Sidebar */}
      <aside className={`${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:sticky top-0 h-screen z-40 w-64 bg-cream-2 border-r border-rose/40 transition-transform flex flex-col`}>
        <div className="p-5 flex items-center shrink-0">
          <Link to="/" aria-label="Osho Universal home">
            <img src={oshoLogo} alt="Osho Universal" className="h-10 w-auto" />
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
          {personal.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} className={linkClass}>
              <n.icon className="w-4 h-4 shrink-0" /> {n.label}
            </NavLink>
          ))}

          {adminGroups.length > 0 && (
            <div className="pt-3 mt-3 border-t border-rose/40 space-y-1">
              <div className="px-3 pb-1 text-[10px] uppercase tracking-widest text-mahogany/50">Administration</div>
              {adminGroups.map((g) => (
                <div key={g.id}>
                  <button onClick={() => toggleGroup(g.id)} data-testid={`nav-group-${g.id}`}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs uppercase tracking-widest text-mahogany/70 hover:bg-rose-soft/40 transition-colors">
                    {g.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openGroups[g.id] ? '' : '-rotate-90'}`} />
                  </button>
                  {openGroups[g.id] && (
                    <div className="space-y-1 mt-0.5">
                      {g.items.map((n) => (
                        <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} className={linkClass}>
                          <n.icon className="w-4 h-4 shrink-0" />
                          <span className="flex-1">{n.label}</span>
                          {n.badge === 'inbox' && unread > 0 && (
                            <span data-testid="nav-inbox-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-burgundy text-cream text-[11px] font-medium inline-flex items-center justify-center">{unread > 99 ? '99+' : unread}</span>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </nav>

        <div className="shrink-0 p-4 border-t border-rose/40 bg-cream-2">
          <Link to="/" className="flex items-center gap-2 text-sm text-mahogany/80 hover:text-mahogany mb-3">
            <Home className="w-4 h-4" /> Back to Home
          </Link>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-mahogany/80 hover:text-mahogany">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-rose/40 bg-cream/95 backdrop-blur sticky top-0 z-30 flex items-center justify-between px-5">
          <button className="lg:hidden" onClick={() => setOpen(!open)}>
            {open ? <X className="w-5 h-5 text-mahogany" /> : <Menu className="w-5 h-5 text-mahogany" />}
          </button>
          <Link to="/" data-testid="dashboard-back-home" className="flex items-center gap-2 text-sm text-mahogany/80 hover:text-mahogany group">
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" /> Back to Home
          </Link>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right leading-tight hidden sm:block">
              <div className="text-sm text-burgundy font-medium">{user?.full_name}</div>
              <div className="text-[11px] text-mahogany/70 uppercase tracking-wider">{user?.roles?.join(' · ')}</div>
            </div>
            <div className="w-10 h-10 rounded-full bg-rose-soft flex items-center justify-center text-mahogany font-serif-display">
              {user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover rounded-full" /> : initials}
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {open && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
};

export default DashboardLayout;
